// Safety net for the LLM-produced plan. LLM planners are not 100% reliable —
// they can simply forget to add a filter step. Rather than trust the plan
// blindly, we deterministically check the question for an explicit year
// mention and make sure the plan actually filters on it, using only the
// schema (never the dataset) to decide how.
//
// This does NOT override a plan that already handles time correctly
// (trend / compare / an existing filter targeting a time column) — it only
// fills in the gap when the planner dropped the ball.

const YEAR_REGEX = /\b(19|20)\d{2}\b/g;

function extractYearMentions(question) {
  const matches = String(question).match(YEAR_REGEX) || [];
  return [...new Set(matches.map(Number))];
}

function planAlreadyHandlesTime(steps, timeColumns) {

  return steps.some(step => {

    if (step.tool === "trend" || step.tool === "compare") return true;

    if (step.tool === "filter") {
      const col = String(step.column || "").toLowerCase();
      return timeColumns.some(c => c.toLowerCase() === col) ||
             col.includes("year");
    }

    return false;
  });
}

function pickYearColumn(schema) {

  const timeColumns = schema?.time || [];

  // Prefer a column literally named "year" (or containing it) — filtering
  // it needs no datePart extraction.
  const literalYear = timeColumns.find(c => c.toLowerCase().includes("year")) ||
    (schema?.columns || []).find(c => c.toLowerCase() === "year");

  if (literalYear) {
    return { column: literalYear, datePart: null };
  }

  // Otherwise fall back to the first known date/time column and extract
  // the year part out of it.
  if (timeColumns.length) {
    return { column: timeColumns[0], datePart: "year" };
  }

  return null;
}

export function applyYearGuard(question, schema, plan) {

  if (!plan?.steps?.length) return plan;

  const years = extractYearMentions(question);

  if (!years.length) return plan;

  const timeColumns = schema?.time || [];

  if (planAlreadyHandlesTime(plan.steps, timeColumns)) {
    return plan; // planner already dealt with time correctly, don't interfere
  }

  const target = pickYearColumn(schema);

  if (!target) return plan; // no usable time column in this dataset at all

  const filterStep = {
    tool: "filter",
    column: target.column,
    operator: "=",
    value: years[0],
    ...(target.datePart ? { datePart: target.datePart } : {}),
  };

  return {
    ...plan,
    steps: [filterStep, ...plan.steps],
  };
}
